import {
  Box,
  Skeleton,
  Table,
  Thead,
  Th,
  Tr,
  Td,
  Tbody,
  Flex,
  Text,
  Divider,
  Spacer,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { VerificationStatusIcon } from './VerificationIcons';
import {
  verificationFundingRequestTableIfApprovedHeaderWidth,
  verificationFundingRequestTableNotesHeaderWidth,
  verificationTablesCellWidth,
} from './VerificationTableSharedComponents';
import {
  isSanitizedJSONWithNumberValues,
  VerificationApproveTablesMode,
} from './verificationTablesUtils';
import { verificationRawValueRoundingErrorAllowance } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { displayValueOrEmpty } from 'packages/common-frontend/utils';
import { useGetApprovedFundingRequestsMetadataWithBorrowingBaseReportQuery } from 'packages/funding-requests/requests/requests.gql.generated';

type LimitType = 'min' | 'max';

export enum VerificationReportFundingRequestComponentTypes {
  drawRequestTotalAmount = 'drawRequestTotalAmount',
  collectionsRecycleTotalAmount = 'collectionsRecycleTotalAmount',
  collectionsPaydownTotalAmount = 'collectionsPaydownTotalAmount',
  curePaydownTotalAmount = 'curePaydownTotalAmount',
}

export const isVerificationReportFundingRequestComponentTypes = (
  test: any,
): test is VerificationReportFundingRequestComponentTypes => {
  return _.includes(VerificationReportFundingRequestComponentTypes, test);
};

const generateLimitNotesString = ({
  constraintKey,
  limitNotes,
}: {
  constraintKey: string;
  limitNotes: {
    limitValue: number;
    limitValueType: 'number' | 'currency';
    limitType: LimitType;
  };
}) => {
  const limitTypeToKeywordMap: { [key in LimitType]: string } = {
    min: 'not meet',
    max: 'exceed',
  };

  const limitValueFormatted =
    limitNotes.limitValueType === 'currency'
      ? commonUtils.numberToCurrencyWithSymbol({
          number: limitNotes.limitValue,
        })
      : limitNotes.limitValue;

  return `${limitValueFormatted} would ${
    limitTypeToKeywordMap[limitNotes.limitType]
  } ${_.toLower(commonUtils.toSentenceCase(constraintKey))}.`;
};

export const generateIncrementAmountLimitNotes = ({
  fundingRequestComponentAmount,
  fundingRequestComponentType,
  incrementAmount,
}: {
  fundingRequestComponentAmount: number;
  fundingRequestComponentType: VerificationReportFundingRequestComponentTypes;
  incrementAmount: number;
}) => {
  const fundingRequestComponentAmountForIncrement =
    Math.floor(fundingRequestComponentAmount / incrementAmount) *
    incrementAmount;

  return `${commonUtils.toSentenceCase(
    fundingRequestComponentType,
  )} must have an increment amount of ${commonUtils.numberToCurrencyWithSymbol({
    number: incrementAmount,
  })}. For example: ${commonUtils.numberToCurrencyWithSymbol({
    number: fundingRequestComponentAmountForIncrement,
  })}.`;
};

const isMaximumFrequencyApproved = ({
  maximumFrequency,
  currentCount,
}: {
  maximumFrequency: number;
  currentCount: number;
}): boolean => currentCount + 1 <= maximumFrequency;

const isMaximumAmountPerPeriodApproved = ({
  maximumAmountPerPeriod,
  requestAmount,
  currentPeriodAmount,
}: {
  maximumAmountPerPeriod: number;
  requestAmount: number;
  currentPeriodAmount: number;
}): boolean => currentPeriodAmount + requestAmount <= maximumAmountPerPeriod;

export const isIncrementAmountApproved = ({
  fundingRequestComponentAmount,
  incrementAmount,
}: {
  fundingRequestComponentAmount: number;
  incrementAmount: number;
}): boolean => fundingRequestComponentAmount % incrementAmount === 0;

export const isMinimumAmountApproved = ({
  fundingRequestComponentAmount,
  minimumAmount,
}: {
  fundingRequestComponentAmount: number;
  minimumAmount: number;
}): { isApproved: boolean; isRoundingErrorAllowanceUsed: boolean } => {
  const isApproved =
    fundingRequestComponentAmount >=
    minimumAmount - verificationRawValueRoundingErrorAllowance;
  return {
    isApproved,
    isRoundingErrorAllowanceUsed:
      isApproved && fundingRequestComponentAmount < minimumAmount,
  };
};

export const isFundingRequestComponentMaximumConstraintAmountApproved = ({
  fundingRequestComponentAmount,
  fundingRequestComponentMaximumConstraintAmount,
}: {
  fundingRequestComponentAmount: number;
  fundingRequestComponentMaximumConstraintAmount: number;
}): { isApproved: boolean; isRoundingErrorAllowanceUsed: boolean } => {
  const isApproved =
    fundingRequestComponentAmount <=
    fundingRequestComponentMaximumConstraintAmount +
      verificationRawValueRoundingErrorAllowance;
  return {
    isApproved,
    isRoundingErrorAllowanceUsed:
      isApproved &&
      fundingRequestComponentAmount >
        fundingRequestComponentMaximumConstraintAmount,
  };
};

export const getCalendarDateRangeForFrequency = ({
  reportingDate,
  frequency,
}: {
  reportingDate: Util.FullDateStr;
  frequency: Util.FrequencyPeriod;
}): { startDate: number; endDate: number } => {
  const endDate = commonDate.fullDateStrToEpoch(reportingDate);
  return {
    startDate: commonDate.fullDateStrToEpoch(
      commonDate.firstOfPeriod(reportingDate, frequency),
    ),
    endDate,
  };
};

const isReportingDateWithinDateRange = ({
  reportingDate,
  startDateEpoch,
  endDateEpoch,
}: {
  reportingDate: Util.FullDateStr;
  startDateEpoch: number;
  endDateEpoch: number;
}): boolean => {
  return (
    commonDate.fullDateStrToEpoch(reportingDate) <= endDateEpoch &&
    commonDate.fullDateStrToEpoch(reportingDate) >= startDateEpoch
  );
};

const VerificationFundingConstraintValidCell = () => {
  return (
    <Td
      bg={colors.green[100]}
      border="none"
      width={verificationFundingRequestTableIfApprovedHeaderWidth}
      padding="8px"
      borderLeftRadius="8px"
    >
      <Flex>
        <VerificationStatusIcon status="success" />
        <Box w="16px" />
        <Text color={colors.success} fontWeight="bold">
          Valid
        </Text>
      </Flex>
    </Td>
  );
};

const VerificationFundingConstraintInvalidCell = () => {
  return (
    <Td
      bg={colors.red[100]}
      border="none"
      width={verificationFundingRequestTableIfApprovedHeaderWidth}
      padding="8px"
      borderLeftRadius="8px"
    >
      <Flex>
        <VerificationStatusIcon status="error" />
        <Box w="16px" />
        <Text color={colors.error} fontWeight="bold">
          Not Valid
        </Text>
      </Flex>
    </Td>
  );
};

export const VerificationFundingConstraintRow: React.FC<{
  constraintKey: string;
  requirement?: string;
  current?: string;
  ifApproved: boolean;
  isRoundingErrorAllowanceUsed?: boolean;
  // the limitNotes obj will be used to construct the notes section if ifApproved===false
  // to explain why it is invalid
  limitNotes: {
    limitValue: number;
    limitValueType: 'number' | 'currency';
    limitType: LimitType;
  };
  limitNotesCustomString?: string;
}> = ({
  constraintKey,
  requirement,
  current,
  ifApproved,
  isRoundingErrorAllowanceUsed,
  limitNotes,
  limitNotesCustomString,
}) => {
  let limitNotesString: string = '';
  if (!ifApproved) {
    limitNotesString =
      limitNotesCustomString ??
      generateLimitNotesString({ constraintKey, limitNotes });
  }
  if (isRoundingErrorAllowanceUsed) {
    limitNotesString = `The difference in values is ${verificationRawValueRoundingErrorAllowance} or less`;
  }

  return (
    <Table
      variant="unstyled"
      style={{ tableLayout: 'fixed', cursor: 'default' }}
      mt="16px"
    >
      <Tbody>
        <Tr>
          <Td border="none" width={verificationTablesCellWidth} padding="8px">
            {commonUtils.toTitleCase(constraintKey)}
          </Td>
          <Td border="none" width={verificationTablesCellWidth} padding="8px">
            {displayValueOrEmpty(requirement)}
          </Td>
          <Td border="none" width={verificationTablesCellWidth} padding="8px">
            {displayValueOrEmpty(current)}
          </Td>
          {ifApproved ? (
            <VerificationFundingConstraintValidCell />
          ) : (
            <VerificationFundingConstraintInvalidCell />
          )}
          <Td
            border="none"
            bg={ifApproved ? colors.green[100] : colors.red[100]}
            color={ifApproved ? colors.success : colors.error}
            width={verificationFundingRequestTableNotesHeaderWidth}
            padding="8px"
            borderRightRadius="8px"
          >
            {limitNotesString}
          </Td>
        </Tr>
      </Tbody>
    </Table>
  );
};

export const VerificationFundingRequestTableHeader: React.FC<{
  label?: string;
  width?: string;
}> = ({ label, width }) => {
  return (
    <Th
      bg="white"
      textTransform="none"
      fontSize="16px"
      fontWeight="semibold"
      w={width || verificationTablesCellWidth}
    >
      {label || ''}
    </Th>
  );
};

export const MaximumAmountPerPeriodRow: React.FC<{
  fundingRequestComponentTotalAmount: number;
  fundingRequestComponentTypeKey: VerificationReportFundingRequestComponentTypes;
  maximumAmountPeriod: Util.FrequencyPeriod;
  maximumAmountPerPeriod: number;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
}> = ({
  fundingRequestComponentTotalAmount,
  fundingRequestComponentTypeKey,
  maximumAmountPerPeriod,
  maximumAmountPeriod,
  creditFacilityId,
  reportingDate,
}) => {
  const {
    data: approvedFundingRequestsData,
    loading: isApprovedFundingRequestsLoading,
  } = useGetApprovedFundingRequestsMetadataWithBorrowingBaseReportQuery({
    variables: { creditFacilityIds: [creditFacilityId] },
  });

  if (!approvedFundingRequestsData || isApprovedFundingRequestsLoading) {
    return (
      <Skeleton
        height="40px"
        data-testid="verification-draw-request-maximum-amount-per-period-loading"
      />
    );
  }

  const {
    fundingRequests: { nodes },
  } = approvedFundingRequestsData;

  const { startDate, endDate } = getCalendarDateRangeForFrequency({
    reportingDate,
    frequency: maximumAmountPeriod,
  });

  const approvedFundingRequestsWithinDateRange = _.filter(
    nodes,
    (node) =>
      node[fundingRequestComponentTypeKey] &&
      isReportingDateWithinDateRange({
        reportingDate: node.borrowingBaseReport.reportingDate,
        startDateEpoch: startDate,
        endDateEpoch: endDate,
      }),
  );

  const totalFundingRequestComponentAmountForPeriod = _.sumBy(
    approvedFundingRequestsWithinDateRange,
    fundingRequestComponentTypeKey,
  );

  return (
    <VerificationFundingConstraintRow
      constraintKey="maximumAmountPerPeriod"
      requirement={`${commonUtils.numberToCurrencyWithSymbol({
        number: maximumAmountPerPeriod,
      })} per ${maximumAmountPeriod}`}
      current={commonUtils.numberToCurrencyWithSymbol({
        number: totalFundingRequestComponentAmountForPeriod,
      })}
      ifApproved={isMaximumAmountPerPeriodApproved({
        maximumAmountPerPeriod,
        requestAmount: fundingRequestComponentTotalAmount,
        currentPeriodAmount: totalFundingRequestComponentAmountForPeriod,
      })}
      limitNotes={{
        limitValue:
          totalFundingRequestComponentAmountForPeriod +
          fundingRequestComponentTotalAmount,
        limitValueType: 'currency',
        limitType: 'max',
      }}
    />
  );
};

export const MaximumFrequencyRow: React.FC<{
  creditFacilityId: string;
  maximumFrequency: CreditAgreement.DomainEntity.MaximumFrequencyProperty;
  reportingDate: Util.FullDateStr;
  fundingRequestComponentTypeKey: VerificationReportFundingRequestComponentTypes;
}> = ({
  creditFacilityId,
  maximumFrequency,
  reportingDate,
  fundingRequestComponentTypeKey,
}) => {
  const {
    data: approvedFundingRequestsData,
    loading: isApprovedFundingRequestsLoading,
  } = useGetApprovedFundingRequestsMetadataWithBorrowingBaseReportQuery({
    variables: { creditFacilityIds: [creditFacilityId] },
  });

  if (!approvedFundingRequestsData || isApprovedFundingRequestsLoading) {
    return (
      <Skeleton
        height="40px"
        data-testid="verification-funding-request-component-maximum-amount-per-period-loading"
      />
    );
  }

  const {
    fundingRequests: { nodes },
  } = approvedFundingRequestsData;

  const { value, frequencyType, frequencyPeriod } = maximumFrequency;

  const { startDate, endDate } = getCalendarDateRangeForFrequency({
    reportingDate,
    frequency: frequencyPeriod,
  });

  const totalFundingRequestComponentCountForPeriod = _.filter(
    nodes,
    (node) =>
      node[fundingRequestComponentTypeKey] &&
      node[fundingRequestComponentTypeKey] > 0 &&
      isReportingDateWithinDateRange({
        reportingDate: node.borrowingBaseReport.reportingDate,
        startDateEpoch: startDate,
        endDateEpoch: endDate,
      }),
  ).length;

  return (
    <VerificationFundingConstraintRow
      constraintKey="maximumFrequency"
      requirement={`${value} per ${frequencyType} ${frequencyPeriod}`}
      current={_.toString(totalFundingRequestComponentCountForPeriod)}
      ifApproved={isMaximumFrequencyApproved({
        maximumFrequency: value,
        currentCount: totalFundingRequestComponentCountForPeriod,
      })}
      limitNotes={{
        limitValue: totalFundingRequestComponentCountForPeriod + 1,
        limitValueType: 'number',
        limitType: 'max',
      }}
    />
  );
};

export const CurrencyRowWithDivider: React.FC<{
  label: string;
  value: number;
}> = ({ label, value }) => {
  return (
    <Box mb="16px">
      <Flex mb="8px">
        <Text fontSize="18px" fontWeight={600}>
          {commonUtils.toTitleCase(label)}
        </Text>
        <Spacer />
        <Text fontSize="18px" fontWeight={600}>
          {commonUtils.numberToCurrencyWithSymbol({ number: value })}
        </Text>
      </Flex>
      <Divider opacity="100%" />
    </Box>
  );
};

export const VerificationFundingRequestComponentTable: React.FC<{
  fundingRequestComponentRows: React.ReactElement;
  fundingRequestComponentType: string;
  fundingRequestComponentTotalAmount: Util.SanitizedJSON;
  viewMode: VerificationApproveTablesMode;
}> = ({
  fundingRequestComponentRows,
  fundingRequestComponentType,
  fundingRequestComponentTotalAmount,
  viewMode,
}) => {
  return (
    <Box p="0 16px" mb="48px">
      {isSanitizedJSONWithNumberValues(fundingRequestComponentTotalAmount) && (
        <Box>
          <CurrencyRowWithDivider
            key={`funding-component-total-${fundingRequestComponentType}`}
            label={fundingRequestComponentType}
            value={fundingRequestComponentTotalAmount.borrowerValue ?? 0}
          />

          {viewMode === 'approveDeny' && (
            <>
              <Table
                variant="simple"
                style={{ tableLayout: 'fixed', cursor: 'default' }}
              >
                <Thead>
                  <Tr bg="white">
                    <VerificationFundingRequestTableHeader />
                    <VerificationFundingRequestTableHeader label="Requirement" />
                    <VerificationFundingRequestTableHeader label="Current" />
                    <VerificationFundingRequestTableHeader
                      label="If Approved"
                      width={
                        verificationFundingRequestTableIfApprovedHeaderWidth
                      }
                    />
                    <VerificationFundingRequestTableHeader
                      width={verificationFundingRequestTableNotesHeaderWidth}
                    />
                  </Tr>
                </Thead>
              </Table>
              {fundingRequestComponentRows}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
